import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Button,
  Spinner,
  Chip,
  Collapse,
} from '@material-tailwind/react';
import {
  SparklesIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

import { getDefaultGuidelines } from '../../services/reportApi';

function AISettingsPanel({ draft, onGuidelinesChange, onHasChanges, onError }) {
  const originalGuidelines = draft?.customGuidelines || '';

  const [guidelines, setGuidelines] = useState(originalGuidelines);
  const [defaultGuidelines, setDefaultGuidelines] = useState('');
  const [showFullPrompt, setShowFullPrompt] = useState(false);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);

  // Fetch default guidelines on mount
  useEffect(() => {
    const fetchDefaults = async () => {
      try {
        const result = await getDefaultGuidelines();
        setDefaultGuidelines(result.guidelines || '');
      } catch (err) {
        console.error('Failed to fetch default guidelines:', err);
      }
    };
    fetchDefaults();
  }, []);

  // Initialize guidelines with defaults if empty
  useEffect(() => {
    if (!guidelines && defaultGuidelines && !originalGuidelines) {
      setGuidelines(defaultGuidelines);
    }
  }, [defaultGuidelines, guidelines, originalGuidelines]);

  // Reset when switching drafts
  useEffect(() => {
    setGuidelines(draft?.customGuidelines || defaultGuidelines || '');
  }, [defaultGuidelines, draft?.customGuidelines, draft.draftId]);

  const guidelinesChanged = guidelines !== (originalGuidelines || defaultGuidelines);

  // Notify parent of change status
  useEffect(() => {
    if (onHasChanges) {
      onHasChanges(guidelinesChanged);
    }
  }, [guidelinesChanged, onHasChanges]);

  // Notify parent of current guidelines value
  useEffect(() => {
    if (onGuidelinesChange) {
      onGuidelinesChange(guidelines);
    }
  }, [guidelines, onGuidelinesChange]);

  const handleResetGuidelines = async () => {
    try {
      setIsLoadingDefaults(true);

      if (defaultGuidelines) {
        setGuidelines(defaultGuidelines);
      } else {
        const result = await getDefaultGuidelines();
        setDefaultGuidelines(result.guidelines || '');
        setGuidelines(result.guidelines || '');
      }
    } catch (err) {
      console.error('Failed to fetch default guidelines:', err);
      if (onError) onError('Failed to load default guidelines');
    } finally {
      setIsLoadingDefaults(false);
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardBody className="flex-1 flex flex-col p-4 overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <SparklesIcon className="h-5 w-5 text-purple-500" />
            <Typography variant="h6" color="blue-gray">
              AI Settings
            </Typography>
          </div>
          {guidelinesChanged && (
            <Chip
              size="sm"
              value="Unsaved Changes"
              color="amber"
              variant="ghost"
            />
          )}
        </div>

        {/* Full AI Prompt - Collapsible Read-only */}
        {draft?.aiPrompt && (
          <div className="mb-4 border rounded-lg overflow-hidden">
            <button
              className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              onClick={() => setShowFullPrompt(!showFullPrompt)}
            >
              <div className="flex items-center gap-2">
                <DocumentTextIcon className="h-4 w-4 text-gray-500" />
                <Typography variant="small" className="font-medium text-gray-700">
                  Full AI Prompt (auto-generated)
                </Typography>
              </div>
              {showFullPrompt ? (
                <ChevronUpIcon className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 text-gray-500" />
              )}
            </button>
            <Collapse open={showFullPrompt}>
              <div className="p-3 bg-gray-50 max-h-64 overflow-auto">
                <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono">
                  {draft.aiPrompt}
                </pre>
              </div>
            </Collapse>
          </div>
        )}

        {/* Editable Guidelines */}
        <div className="flex-1 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <Typography variant="small" className="font-semibold text-gray-700">
              AI Guidelines
              {guidelinesChanged && (
                <span className="ml-2 text-amber-600 font-normal">(modified)</span>
              )}
            </Typography>
            <Button
              variant="text"
              size="sm"
              onClick={handleResetGuidelines}
              disabled={isLoadingDefaults}
              className="flex items-center gap-1 py-1 px-2 text-xs"
            >
              {isLoadingDefaults ? (
                <Spinner className="h-3 w-3" />
              ) : (
                <ArrowPathIcon className="h-3 w-3" />
              )}
              Reset to Defaults
            </Button>
          </div>

          <textarea
            value={guidelines}
            onChange={(e) => setGuidelines(e.target.value)}
            className={`flex-1 w-full p-3 border rounded-lg font-mono text-xs resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[200px] ${
              guidelinesChanged ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
            }`}
            placeholder="Enter AI guidelines for generating report commentary..."
          />

          <div className="flex items-center justify-between mt-2">
            <Typography variant="small" color="gray" className="text-xs">
              {guidelines.length.toLocaleString()} characters
            </Typography>
          </div>
        </div>

        <Typography variant="small" color="gray" className="mt-4 text-xs">
          Guidelines control how the AI generates commentary for your report. Use the Refresh button in the header to apply changes.
        </Typography>
      </CardBody>
    </Card>
  );
}

export default AISettingsPanel;
